<template>
  <v-card tile>
    <v-card-text>
      <v-row>
        <v-col md="8">
          <CardLayout title="Credentials" flush>
            <template #header-actions>
              <v-btn
                color="primary"
                small
                :disabled="!currentFacilityId"
                @click="openDialog"
              >
                <v-icon small left>mdi-download</v-icon>
                Generate New Credential
              </v-btn>
            </template>

            <v-data-table
              :headers="headers"
              :items="keys"
              item-key="id"
              no-data-text="No credentials generated yet."
              hide-default-footer
            >
              <template #[`item.status`]="{ item }">
                <v-chip
                  x-small
                  :color="item.status === 'active' ? 'success' : 'warning'"
                  text-color="white"
                >
                  {{ titleCase(item.status) }}
                </v-chip>
              </template>
              <template #[`item.downloadedAt`]="{ item }">
                {{ formatDownloaded(item.downloadedAt) }}
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn
                  v-if="item.status === 'active'"
                  color="error"
                  small
                  outlined
                  :loading="revoking"
                  @click="openRevoke(item)"
                >
                  Revoke
                </v-btn>
                <span v-else class="grey--text">--</span>
              </template>
            </v-data-table>
          </CardLayout>
        </v-col>
      </v-row>
    </v-card-text>

    <ApiSettingsDialog
      v-model="dialogOpen"
      :organization-id="currentFacilityId"
      :current="latestKey"
      @generated="onGenerated"
    />

    <v-dialog v-model="revokeDialog" max-width="600px">
      <v-card>
        <v-card-title class="headline">
          <span class="red--text">WARNING</span>
          <v-spacer />
          <v-btn icon @click="revokeDialog = false">
            <v-icon>mdi-close-circle</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="mt-4">
          Are you sure you want to revoke this API credential? Any partner
          integration using it will stop working immediately.
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="revokeDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="error" text :loading="revoking" @click="confirmRevoke">
            Revoke
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { titleCase } from "@tod-ui/helpers/strings";
import CardLayout from "@layout/CardLayout.vue";
import useFacility from "../authentication/useFacility";
import { useApiIntegration, resolveSdApiClient } from "./useApiIntegration";
import ApiSettingsDialog from "./ApiSettingsDialog.vue";

export default {
  name: "ApiSettingsCard",
  components: { CardLayout, ApiSettingsDialog },
  setup() {
    const { currentFacilityId, facilityFormatDateTime4Humans } = useFacility();
    const { keys, revoking, latestKey, fetchKeys, revokeKey } =
      useApiIntegration(currentFacilityId);
    return {
      currentFacilityId,
      facilityFormatDateTime4Humans,
      keys,
      revoking,
      latestKey,
      fetchKeys,
      revokeKey,
      titleCase,
    };
  },
  data() {
    return {
      dialogOpen: false,
      revokeDialog: false,
      revokeTarget: null,
      headers: [
        { text: "Name", value: "name", sortable: false, align: "start" },
        { text: "Status", value: "status", sortable: false, align: "center" },
        {
          text: "Last Downloaded",
          value: "downloadedAt",
          sortable: false,
          align: "center",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
    };
  },
  computed: {
    sdApiClient() {
      return resolveSdApiClient(this.$apollo);
    },
  },
  watch: {
    currentFacilityId: {
      immediate: true,
      handler(id) {
        if (id) this.fetchKeys(this.sdApiClient);
      },
    },
  },
  methods: {
    formatDownloaded(value) {
      return value
        ? this.facilityFormatDateTime4Humans(value, false, false, false)
        : "--";
    },
    openDialog() {
      this.dialogOpen = true;
    },
    async onGenerated({ downloadUrl }) {
      this.triggerDownload(downloadUrl);
      // One refetch reflects the new active key + the previous one now revoked.
      await this.fetchKeys(this.sdApiClient);
      // The download GET has already stamped downloaded_at server-side, so show it
      // now optimistically rather than polling for the async write to land.
      const active = this.keys.find((key) => key.status === "active");
      if (active && !active.downloadedAt) {
        active.downloadedAt = new Date().toISOString();
      }
    },
    triggerDownload(downloadUrl) {
      const link = document.createElement("a");
      link.href = downloadUrl;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    openRevoke(item) {
      this.revokeTarget = item;
      this.revokeDialog = true;
    },
    async confirmRevoke() {
      await this.revokeKey(this.sdApiClient, this.revokeTarget?.id);
      this.revokeDialog = false;
      this.revokeTarget = null;
    },
  },
};
</script>
